export const AUDIO_SCENES = [
  "home",
  "lobby",
  "role_reveal",
  "night",
  "morning",
  "discussion",
  "voting",
  "ended_villagers",
  "ended_wolves",
] as const;

export type AudioScene = (typeof AUDIO_SCENES)[number];
export type AudioBus = "music" | "ambience" | "sfx";

export interface AudioCredit {
  title: string;
  creator: string;
  license: string;
  sourceUrl: string;
  licenseUrl?: string;
}

export const AUDIO_CREDITS: readonly AudioCredit[] = [
  { title: "Whisper3", creator: "PeriTune", license: "CC BY 4.0", sourceUrl: "https://peritune.com/blog/2018/07/09/whisper3/", licenseUrl: "https://creativecommons.org/licenses/by/4.0/" },
  { title: "Suspense4", creator: "PeriTune", license: "CC BY 4.0", sourceUrl: "https://peritune.com/blog/2020/01/13/suspense4/", licenseUrl: "https://creativecommons.org/licenses/by/4.0/" },
  { title: "Investigation2", creator: "PeriTune", license: "CC BY 4.0", sourceUrl: "https://peritune.com/blog/2016/01/17/investigation2/", licenseUrl: "https://creativecommons.org/licenses/by/4.0/" },
  { title: "Suspense8", creator: "PeriTune", license: "CC BY 4.0", sourceUrl: "https://peritune.com/blog/2017/04/04/suspense_8/", licenseUrl: "https://creativecommons.org/licenses/by/4.0/" },
  { title: "Interface Sounds", creator: "Kenney", license: "CC0 1.0", sourceUrl: "https://kenney.nl/assets/interface-sounds", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "UI Audio", creator: "Kenney", license: "CC0 1.0", sourceUrl: "https://kenney.nl/assets/ui-audio", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Music Jingles", creator: "Kenney", license: "CC0 1.0", sourceUrl: "https://kenney.nl/assets/music-jingles", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Dark Rainy Night (ambience)", creator: "kindland", license: "CC0 1.0", sourceUrl: "https://opengameart.org/content/rain-and-thunders", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Forest Ambience", creator: "TinyWorlds", license: "CC0 1.0", sourceUrl: "https://opengameart.org/content/forest-ambience", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Ambient Bird Sounds", creator: "isaiah658", license: "CC0 1.0", sourceUrl: "https://opengameart.org/content/ambient-bird-sounds", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Wolf Monster Sound", creator: "CaveboyTup", license: "CC0 1.0", sourceUrl: "https://opengameart.org/content/wolf-monster-sound", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
  { title: "Gunshot Sounds", creator: "Tabasco", license: "CC0 1.0", sourceUrl: "https://opengameart.org/content/gunshot-sounds", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/" },
];

export interface AudioSource {
  src: string;
  type: "audio/ogg" | "audio/mpeg";
}

export interface AudioVariant {
  sources: readonly AudioSource[];
}

interface AudioDefinition {
  bus: AudioBus;
  gain: number;
  provenanceId: string;
  variants: readonly AudioVariant[];
}

export interface BackgroundDefinition extends AudioDefinition {
  bus: "music" | "ambience";
  loop: true;
  loopStart?: number;
  loopEnd?: number;
}

export interface SfxDefinition extends AudioDefinition {
  bus: "sfx";
  cooldownMs?: number;
}

function sources(path: string): AudioVariant {
  return {
    sources: [
      { src: `${path}.ogg`, type: "audio/ogg" },
      { src: `${path}.mp3`, type: "audio/mpeg" },
    ],
  };
}

const music = (id: string, gain = 1): BackgroundDefinition => ({
  bus: "music",
  gain,
  loop: true,
  provenanceId: `music.${id}`,
  variants: [sources(`/assets/audio/music/${id}`)],
});

const ambience = (id: string, gain = 1): BackgroundDefinition => ({
  bus: "ambience",
  gain,
  loop: true,
  provenanceId: `ambience.${id}`,
  variants: [sources(`/assets/audio/ambience/${id}`)],
});

const sfx = (
  id: string,
  options: { gain?: number; cooldownMs?: number; variants?: readonly string[] } = {},
): SfxDefinition => ({
  bus: "sfx",
  gain: options.gain ?? 1,
  provenanceId: `sfx.${id}`,
  variants: (options.variants ?? [id]).map((variant) => sources(`/assets/audio/sfx/${variant}`)),
  ...(options.cooldownMs === undefined ? {} : { cooldownMs: options.cooldownMs }),
});

export const BACKGROUND_TRACKS = {
  lobby: music("lobby", 0.82),
  night: music("night", 0.78),
  discussion: music("discussion", 0.72),
  voting: music("voting", 0.8),
  village_night: ambience("village-night", 0.9),
  forest_night: ambience("forest-night", 0.9),
  village_dawn: ambience("village-dawn", 0.88),
} as const satisfies Record<string, BackgroundDefinition>;

export type BackgroundTrackId = keyof typeof BACKGROUND_TRACKS;

export interface SceneDefinition {
  music?: BackgroundTrackId;
  ambience?: BackgroundTrackId;
  preload: readonly AudioScene[];
}

export const SCENE_CATALOG = {
  home: { music: "lobby", ambience: "village_night", preload: ["lobby"] },
  lobby: { music: "lobby", ambience: "village_night", preload: ["role_reveal", "night"] },
  role_reveal: { music: "night", ambience: "forest_night", preload: ["night"] },
  night: { music: "night", ambience: "forest_night", preload: ["morning"] },
  morning: { music: "discussion", ambience: "village_dawn", preload: ["discussion"] },
  discussion: { music: "discussion", ambience: "village_dawn", preload: ["voting"] },
  voting: { music: "voting", ambience: "village_dawn", preload: ["night"] },
  ended_villagers: { music: "discussion", ambience: "village_dawn", preload: ["lobby"] },
  ended_wolves: { music: "night", ambience: "forest_night", preload: ["lobby"] },
} as const satisfies Record<AudioScene, SceneDefinition>;

export const SFX_CATALOG = {
  sound_on: sfx("sound-on", { gain: 0.72, cooldownMs: 250 }),
  ui_hover: sfx("ui-hover", {
    gain: 0.38,
    cooldownMs: 70,
    variants: ["ui-hover-01", "ui-hover-02"],
  }),
  ui_click: sfx("ui-click", {
    gain: 0.58,
    cooldownMs: 45,
    variants: ["ui-click-01", "ui-click-02"],
  }),
  room_create: sfx("room-create", { gain: 0.82, cooldownMs: 300 }),
  room_join_success: sfx("room-join-success", { gain: 0.82, cooldownMs: 300 }),
  room_join_failure: sfx("room-join-failure", { gain: 0.72, cooldownMs: 300 }),
  invite_copy: sfx("invite-copy", { gain: 0.65, cooldownMs: 250 }),
  player_join: sfx("player-join", { gain: 0.7, cooldownMs: 120 }),
  player_leave: sfx("player-leave", { gain: 0.62, cooldownMs: 120 }),
  player_ready: sfx("player-ready", { gain: 0.72, cooldownMs: 100 }),
  all_ready: sfx("all-ready", { gain: 0.85, cooldownMs: 500 }),
  game_start: sfx("game-start", { gain: 0.92, cooldownMs: 1_000 }),
  phase_change: sfx("phase-change", { gain: 0.72, cooldownMs: 250 }),
  role_guard: sfx("role-guard", { gain: 0.78, cooldownMs: 500 }),
  role_wolf: sfx("role-wolf", { gain: 0.78, cooldownMs: 500 }),
  role_seer: sfx("role-seer", { gain: 0.78, cooldownMs: 500 }),
  role_witch: sfx("role-witch", { gain: 0.78, cooldownMs: 500 }),
  target_select: sfx("target-select", {
    gain: 0.56,
    cooldownMs: 75,
    variants: ["target-select-01", "target-select-02"],
  }),
  action_confirm: sfx("action-confirm", { gain: 0.74, cooldownMs: 150 }),
  vote_cast: sfx("vote-cast", {
    gain: 0.7,
    cooldownMs: 100,
    variants: ["vote-cast-01", "vote-cast-02"],
  }),
  countdown_tick: sfx("countdown-tick", { gain: 0.48, cooldownMs: 500 }),
  timer_expired: sfx("timer-expired", { gain: 0.82, cooldownMs: 750 }),
  elimination: sfx("elimination", { gain: 0.84, cooldownMs: 500 }),
  vote_tie: sfx("vote-tie", { gain: 0.74, cooldownMs: 500 }),
  hunter_shot: sfx("hunter-shot", { gain: 0.8, cooldownMs: 750 }),
  hunter_skip: sfx("hunter-skip", { gain: 0.62, cooldownMs: 500 }),
  morning_safe: sfx("morning-safe", { gain: 0.78, cooldownMs: 750 }),
  morning_death: sfx("morning-death", { gain: 0.82, cooldownMs: 750 }),
  villagers_win: sfx("villagers-win", { gain: 0.9, cooldownMs: 1_000 }),
  wolves_win: sfx("wolves-win", { gain: 0.9, cooldownMs: 1_000 }),
  wolf_howl: sfx("wolf-howl", {
    gain: 0.56,
    cooldownMs: 5_000,
    variants: ["wolf-howl-01", "wolf-howl-02", "wolf-howl-03"],
  }),
} as const satisfies Record<string, SfxDefinition>;

export type SfxId = keyof typeof SFX_CATALOG;
